import { Component, forwardRef, Input, OnDestroy, OnInit } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BehaviorSubject, debounceTime, distinctUntilChanged, map, Observable, Subscription, switchMap } from 'rxjs';

export interface AutocompletePagination {
    page: number;
    itemPerPage: number;
}

export interface AutocompleteApiResponse<T> {
    items: T[];
    totalSize: number;
}

/** Displayed entities must comply with this interface. */
export interface Displayable { id: string; name: string };

export type AutocompleteDataLoader<T extends Displayable> = (filter: string, pagination: AutocompletePagination) => Observable<AutocompleteApiResponse<T>>;
const AUTOCOMPLETE_ITEM_PER_PAGE = 5; // Can be tweaked.
const INITIAL_PAGE = 1;

/**
 * Addresses the cases where the name needs to be displayed out of the autocomplete component.
 * This component will keep track of all the resolved entities during the lifetime of the form.
 * How to use :
 * 1) During the loading phase, call the "listen" method and pass the displayables with the matching filter.
 * 2) Resolve the IDs with the "solve" method directly in the controller.
 */
export class EntityNameResolver {
    private entities: Record<string, string> = {};

    listen(strictSearch: boolean, displayables: Displayable[]): void {
        if (!strictSearch || displayables.length !== 1) {
            return;
        }

        const { id, name } = displayables[0];
        this.entities[id] = name;
    }

    solve(id: string): string | undefined {
        return this.entities[id];
    }
}

/** Used for manual id input validation. */
const ID_MAX_LENGTH = 6;

@Component({
    selector: 'syn-autocomplete',
    templateUrl: './autocomplete.component.html',
    styleUrls: ['./autocomplete.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AutocompleteComponent),
            multi: true
        }
    ]
})
export class AutocompleteComponent<T extends Displayable> implements ControlValueAccessor, OnInit, OnDestroy {

    @Input() readonly label: string;
    @Input() readonly hint: string;
    @Input() readonly saveIndicator = true;
    // When using a different regex, don't forget to specify the "capture" group.
    @Input() readonly idForInvalidRegex = new RegExp(`^[A-Z]{3}-([A-Z0-9]{0,${ID_MAX_LENGTH - 1}})$`);
    @Input() readonly dataLoader: AutocompleteDataLoader<T>;

    items: T[] | undefined;
    totalSize: number;
    loading = false;

    searchCtrl = new FormControl<string>('');
    selectedItemId: string | null = null;
    hasInitialValue = false;

    touched = false;

    readonly incompleteId = this.searchCtrl.valueChanges.pipe(map(value => this.isIncompleteId(value)));

    readonly search$ = this.searchCtrl.valueChanges.pipe(
        debounceTime(300),
        distinctUntilChanged()
    );

    private dataLoader$ = new BehaviorSubject<{ query: string; page: number }>({ query: '', page: INITIAL_PAGE });

    private dataLoaderSubscription: Subscription;
    private searchSubscription: Subscription;

    get isNotReady() {
        return Boolean(this.selectedItemId) && this.items === undefined;
    }

    missingLetters(input: string): number {
        const match = input.match(this.idForInvalidRegex);
        return ID_MAX_LENGTH - match[1].length;
    }

    onChange: (item: string) => void = (item: string) => {};
    onTouched: () => void = () => { };

    readonly displayFn = (itemId: string | null): string => {
        return itemId ? this.items?.find(item => item.id === itemId)?.name : '';
    };

    remainingItems(totalSize: number): number {
        return totalSize - this.dataLoader$.value.page * AUTOCOMPLETE_ITEM_PER_PAGE;
    }

    loadNextPage() {
        const { page, query } = this.dataLoader$.value;
        this.dataLoader$.next({ page: page + 1, query });
    }

    onSelect(id: string) {
        this.selectedItemId = id;
        this.onChange(id);
        this.setTouched();
    }

    onClosed() {
        this.searchCtrl.setValue(this.selectedItemId, { emitEvent: false });
    }

    clear() {
        this.selectedItemId = null;
        this.items = [];

        this.dataLoader$.next({ query: '', page: INITIAL_PAGE });
        this.searchCtrl.reset();
        this.onChange(null);
    }

    ngOnInit(): void {
        this.dataLoaderSubscription = this.dataLoader$.pipe(
            distinctUntilChanged((previous, current) => {
                return previous.page === current.page && previous.query === current.query;
            }),
            switchMap(({ query, page }) => {
                this.loading = true;
                return this.dataLoader(query, { page, itemPerPage: AUTOCOMPLETE_ITEM_PER_PAGE });
            })
        ).subscribe(({ items, totalSize }) => {
            this.items = [...(this.items || []), ...items];
            this.totalSize = totalSize;
            this.loading = false;
        });

        this.searchSubscription = this.search$.subscribe(query => {
            if (typeof query === 'string') {
                if (this.isIncompleteId(query) || this.dataLoader$.value.query === query) {
                    return;
                }

                this.items = [];
                this.dataLoader$.next({ query, page: 1 });
            }
        });
    }

    ngOnDestroy(): void {
        if (this.dataLoaderSubscription) {
            this.dataLoaderSubscription.unsubscribe();
        }

        if (this.searchSubscription) {
            this.searchSubscription.unsubscribe();
        }
    }

    writeValue(value: string): void {
        if (typeof value !== 'string') {
            console.warn('Only "string" values are supported.');
            return;
        }

        if (this.hasInitialValue && !this.touched) {
            this.setTouched();
        }

        this.items = undefined;
        this.selectedItemId = value;
        this.hasInitialValue = true;

        // We need to avoid to empty the items list.
        this.searchCtrl.setValue(value, { emitEvent: false });
        this.dataLoader$.next({ query: value, page: 1 });
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.searchCtrl.disable();
        } else {
            this.searchCtrl.enable();
        }
    }

    setTouched() {
        this.touched = true;
        this.onTouched();
    }

    private isIncompleteId(search: string): boolean {
        if (!search || search.length === 0) {
            return false;
        }
        return this.idForInvalidRegex.test(search);
    }
}
